class InnovaCarouselController {
  constructor(root) {
    this.root = root;
    this.track = root.querySelector('.innova-product-carousel__track');
    if (!this.track) return;

    this.autoplay = root.dataset.autoplay === 'true';
    this.interval = Number(root.dataset.interval || 4600);
    this.timer = null;
    this.pointerCandidate = false;
    this.isDragging = false;
    this.startX = 0;
    this.startScrollLeft = 0;
    this.pointerId = null;
    this.resumeTimer = null;
    this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.interactiveSelector = 'a,button,input,select,textarea,label,form,summary,details,[role="button"]';

    this.bind();
    this.updateProgress();
    if (this.autoplay && !this.reducedMotion) this.startAutoplay();
  }

  bind() {
    this.track.addEventListener('scroll', () => this.updateProgress(), { passive: true });
    this.root.addEventListener('mouseenter', () => this.stopAutoplay());
    this.root.addEventListener('mouseleave', () => this.scheduleAutoplay());
    this.root.addEventListener('focusin', () => this.stopAutoplay());
    this.root.addEventListener('focusout', () => this.scheduleAutoplay());

    // Important: never capture a pointer that starts on a link/button/form.
    // Product navigation remains a normal Shopify <a href="product.url">.
    this.track.addEventListener('pointerdown', (event) => {
      if (event.pointerType === 'touch' || event.button !== 0) return;
      if (event.target.closest(this.interactiveSelector)) return;

      this.pointerCandidate = true;
      this.isDragging = false;
      this.pointerId = event.pointerId;
      this.startX = event.clientX;
      this.startScrollLeft = this.track.scrollLeft;
      this.stopAutoplay();
    });

    this.track.addEventListener('pointermove', (event) => {
      if (!this.pointerCandidate || event.pointerType === 'touch') return;
      const distance = event.clientX - this.startX;

      if (!this.isDragging && Math.abs(distance) > 7) {
        this.isDragging = true;
        this.track.classList.add('is-dragging');
        try { this.track.setPointerCapture?.(event.pointerId); } catch (_) {}
      }

      if (!this.isDragging) return;
      event.preventDefault();
      this.track.scrollLeft = this.startScrollLeft - distance;
    });

    const endDrag = (event) => {
      if (!this.pointerCandidate) return;
      const wasDragging = this.isDragging;
      this.pointerCandidate = false;
      this.isDragging = false;
      this.track.classList.remove('is-dragging');
      try { this.track.releasePointerCapture?.(this.pointerId); } catch (_) {}
      this.pointerId = null;
      if (wasDragging) window.setTimeout(() => this.snapToNearest(), 40);
      this.scheduleAutoplay();
    };

    this.track.addEventListener('pointerup', endDrag);
    this.track.addEventListener('pointercancel', endDrag);

    document.addEventListener('visibilitychange', () => {
      if (document.hidden) this.stopAutoplay();
      else this.scheduleAutoplay();
    });

    window.addEventListener('resize', () => this.updateProgress(), { passive: true });
  }

  getStep() {
    const first = this.track.querySelector('.innova-product-carousel__item, .slider__slide, .grid__item');
    if (!first) return this.track.clientWidth;
    const style = getComputedStyle(this.track);
    const gap = parseFloat(style.columnGap || style.gap || 0) || 0;
    return first.getBoundingClientRect().width + gap;
  }

  snapToNearest() {
    const step = this.getStep();
    if (!step) return;
    const target = Math.round(this.track.scrollLeft / step) * step;
    this.track.scrollTo({ left: target, behavior: this.reducedMotion ? 'auto' : 'smooth' });
  }

  advance() {
    if (this.track.scrollWidth <= this.track.clientWidth + 2) return;
    const step = this.getStep();
    const max = this.track.scrollWidth - this.track.clientWidth;
    const next = this.track.scrollLeft + step;
    if (next >= max - step * 0.35) this.track.scrollTo({ left: 0, behavior: 'smooth' });
    else this.track.scrollBy({ left: step, behavior: 'smooth' });
  }

  startAutoplay() {
    if (!this.autoplay || this.reducedMotion || this.timer) return;
    this.timer = window.setInterval(() => this.advance(), this.interval);
  }

  stopAutoplay() {
    if (this.timer) window.clearInterval(this.timer);
    this.timer = null;
    if (this.resumeTimer) window.clearTimeout(this.resumeTimer);
    this.resumeTimer = null;
  }

  scheduleAutoplay() {
    if (!this.autoplay || this.reducedMotion) return;
    if (this.resumeTimer) window.clearTimeout(this.resumeTimer);
    this.resumeTimer = window.setTimeout(() => this.startAutoplay(), 1400);
  }

  updateProgress() {
    const progress = this.root.querySelector('.innova-carousel-hint span');
    if (!progress) return;
    const max = this.track.scrollWidth - this.track.clientWidth;
    const pct = max <= 1 ? 1 : Math.max(0.12, Math.min(1, this.track.scrollLeft / max));
    progress.style.setProperty('--carousel-progress', `${pct * 100}%`);
    this.root.classList.toggle('is-static', max <= 1);
  }
}

function initInnovaCarousels(scope = document) {
  scope.querySelectorAll('[data-innova-carousel]').forEach((carousel) => {
    if (carousel.dataset.innovaReady === 'true') return;
    carousel.dataset.innovaReady = 'true';
    new InnovaCarouselController(carousel);
  });
}

document.addEventListener('DOMContentLoaded', () => initInnovaCarousels());
document.addEventListener('shopify:section:load', (event) => initInnovaCarousels(event.target));
